/* =========================================================
   ZEPTO STYLE OPTIONS POPUP
   Opens when a product card "N Options" button is clicked and the theme
   setting "When click Select options button" is set to the Zepto popup.
   Desktop: centred modal. Mobile: bottom sheet.
   ========================================================= */
(function () {
  'use strict';

  if (window.ZeptoOptionsLoaded) return;
  window.ZeptoOptionsLoaded = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var LABELS = Object.assign(
    {
      add: 'ADD',
      soldOut: 'Sold out',
      save: 'Save',
      loading: 'Loading options',
      error: 'Could not load the options. Open the product page instead.',
      close: 'Close'
    },
    window.ZeptoOptionsLabels || {}
  );

  var cache = {};
  var root = null;
  var sheet = null;
  var titleEl = null;
  var listEl = null;
  var currentProduct = null;
  var lastFocused = null;

  /* ---------- helpers ---------- */
  function money(cents) {
    return window.ZeptoATC && window.ZeptoATC.money
      ? window.ZeptoATC.money(cents)
      : (cents / 100).toFixed(2);
  }

  // Optimistic overrides so the stepper reacts instantly, like the Zepto app
  var pending = {};

  function cartQtyOf(variantId) {
    return window.ZeptoATC && window.ZeptoATC.quantityOf
      ? window.ZeptoATC.quantityOf(variantId)
      : 0;
  }

  function qtyOf(variantId) {
    var held = pending[variantId];
    return held ? held.qty : cartQtyOf(variantId);
  }

  function sized(url, width) {
    if (!url) return '';
    var full = url.indexOf('//') === 0 ? 'https:' + url : url;
    return full + (full.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  // Variant image, falling back to the product image when the variant has none
  function imageFor(product, variant) {
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    if (variant.featured_media && variant.featured_media.preview_image) {
      return variant.featured_media.preview_image.src;
    }
    if (product.featured_image) return product.featured_image;
    if (product.images && product.images.length) return product.images[0];
    return '';
  }

  function unitPriceOf(variant) {
    if (!variant.unit_price || !variant.unit_price_measurement) return '';
    var m = variant.unit_price_measurement;
    var ref = m.reference_value && m.reference_value !== 1 ? m.reference_value : '';
    return money(variant.unit_price) + '/' + ref + (m.reference_unit || '');
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var closeIcon =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
  var plusIcon =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5.5v13M5.5 12h13" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
  var minusIcon =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5.5 12h13" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';

  /* ---------- build the popup once ---------- */
  function ensureRoot() {
    if (root) return root;

    root = document.createElement('div');
    root.className = 'zepto-opt';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Product options');
    root.innerHTML =
      '<div class="zepto-opt__backdrop" data-zepto-close></div>' +
      '<div class="zepto-opt__sheet">' +
      '<button type="button" class="zepto-opt__close" data-zepto-close aria-label="' + esc(LABELS.close) + '">' + closeIcon + '</button>' +
      '<h2 class="zepto-opt__title" data-zepto-title></h2>' +
      '<div class="zepto-opt__list" data-zepto-list></div>' +
      '</div>';

    document.body.appendChild(root);
    sheet = root.querySelector('.zepto-opt__sheet');
    titleEl = root.querySelector('[data-zepto-title]');
    listEl = root.querySelector('[data-zepto-list]');

    root.addEventListener('click', function (e) {
      var target = e.target instanceof Element ? e.target : null;
      if (!target) return;

      if (target.closest('[data-zepto-close]')) {
        e.preventDefault();
        close();
        return;
      }

      var addBtn = target.closest('[data-zepto-opt-add]');
      if (addBtn) {
        e.preventDefault();
        changeQty(addBtn.closest('[data-variant]'), 1, true);
        return;
      }

      var plus = target.closest('[data-zepto-opt-plus]');
      if (plus) {
        e.preventDefault();
        changeQty(plus.closest('[data-variant]'), 1, false);
        return;
      }

      var minus = target.closest('[data-zepto-opt-minus]');
      if (minus) {
        e.preventDefault();
        changeQty(minus.closest('[data-variant]'), -1, false);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) close();
    });

    if (window.ZeptoATC && window.ZeptoATC.subscribe) {
      window.ZeptoATC.subscribe(function () {
        if (root.classList.contains('is-open')) refreshActions();
      });
    }

    return root;
  }

  /* ---------- open / close ---------- */
  function open(handle, fallbackUrl, fallbackTitle) {
    ensureRoot();
    lastFocused = document.activeElement;
    currentProduct = null;
    pending = {};

    titleEl.textContent = fallbackTitle || '';
    listEl.innerHTML = '<div class="zepto-opt__loading">' + esc(LABELS.loading) + '</div>';

    root.classList.add('is-open');
    document.documentElement.classList.add('zepto-opt-open');

    load(handle)
      .then(function (product) {
        currentProduct = product;
        titleEl.textContent = product.title;
        render(product);
      })
      .catch(function () {
        listEl.innerHTML =
          '<div class="zepto-opt__error">' +
          esc(LABELS.error) +
          (fallbackUrl ? ' <a href="' + esc(fallbackUrl) + '">' + esc(fallbackTitle || '') + '</a>' : '') +
          '</div>';
      });
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('zepto-opt-open');
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function load(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch(ROOT + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(function (product) {
        cache[handle] = product;
        return product;
      });
  }

  /* ---------- rendering ---------- */
  function render(product) {
    var rows = product.variants
      .map(function (variant) {
        var image = sized(imageFor(product, variant), 160);
        var label = variant.title === 'Default Title' ? product.title : variant.title;
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        var unit = unitPriceOf(variant);

        return (
          '<div class="zepto-opt__row' + (onSale ? ' has-save' : '') + '" data-variant="' + variant.id + '">' +
          (onSale
            ? '<span class="zepto-opt__save">' +
              esc(LABELS.save) + ' ' + money(variant.compare_at_price - variant.price) +
              '</span>'
            : '') +
          '<div class="zepto-opt__thumb">' +
          (image ? '<img src="' + esc(image) + '" alt="' + esc(label) + '" loading="lazy" width="80" height="80">' : '') +
          '</div>' +
          '<div class="zepto-opt__label">' + esc(label) + '</div>' +
          '<div class="zepto-opt__prices">' +
          '<div class="zepto-opt__price-row">' +
          '<span class="zepto-opt__price">' + money(variant.price) + '</span>' +
          (onSale ? '<s class="zepto-opt__compare">' + money(variant.compare_at_price) + '</s>' : '') +
          '</div>' +
          (unit ? '<div class="zepto-opt__unit">' + esc(unit) + '</div>' : '') +
          '</div>' +
          '<div class="zepto-opt__action" data-zepto-action>' + actionMarkup(variant) + '</div>' +
          '</div>'
        );
      })
      .join('');

    listEl.innerHTML = rows;
  }

  function actionMarkup(variant) {
    if (!variant.available) {
      return '<button type="button" class="zepto-opt__add is-soldout" disabled>' + esc(LABELS.soldOut) + '</button>';
    }
    var qty = qtyOf(variant.id);
    if (qty > 0) {
      return (
        '<div class="zepto-opt__qty">' +
        '<button type="button" class="zepto-opt__step" data-zepto-opt-minus aria-label="Decrease">' + minusIcon + '</button>' +
        '<span class="zepto-opt__count">' + qty + '</span>' +
        '<button type="button" class="zepto-opt__step" data-zepto-opt-plus aria-label="Increase">' + plusIcon + '</button>' +
        '</div>'
      );
    }
    return '<button type="button" class="zepto-opt__add" data-zepto-opt-add>' + esc(LABELS.add) + '</button>';
  }

  function variantById(id) {
    if (!currentProduct) return null;
    for (var i = 0; i < currentProduct.variants.length; i++) {
      if (currentProduct.variants[i].id === Number(id)) return currentProduct.variants[i];
    }
    return null;
  }

  function refreshActions() {
    if (!currentProduct || !listEl) return;
    listEl.querySelectorAll('[data-variant]').forEach(function (row) {
      var variant = variantById(row.dataset.variant);
      if (!variant) return;
      var holder = row.querySelector('[data-zepto-action]');
      var next = actionMarkup(variant);
      if (holder && holder.innerHTML.trim() !== next.trim()) holder.innerHTML = next;
    });
  }

  /* ---------- cart actions ---------- */
  function changeQty(row, delta, isFirstAdd) {
    if (!row || !window.ZeptoATC) return;
    var variantId = Number(row.dataset.variant);
    var variant = variantById(variantId);
    var max = variant && variant.inventory_management && variant.inventory_policy !== 'continue'
      ? Math.max(1, variant.inventory_quantity)
      : 99;

    var next = Math.max(0, Math.min(max, qtyOf(variantId) + delta));

    // 1. paint the new quantity straight away
    var held = pending[variantId] || {};
    held.qty = next;
    held.open = held.open || (isFirstAdd && CONFIG_OPEN_DRAWER());
    pending[variantId] = held;
    refreshActions();

    // 2. send it after a short pause, so fast taps become one request
    clearTimeout(held.timer);
    held.timer = setTimeout(function () { commit(variantId, row); }, window.ZeptoATC.config.debounce || 320);
  }

  function CONFIG_OPEN_DRAWER() {
    return window.ZeptoATC.config.openDrawerFromOptions === true;
  }

  function commit(variantId, row) {
    var held = pending[variantId];
    if (!held) return;
    var target = held.qty;
    var shouldOpen = held.open;

    row.classList.add('is-loading');
    row.classList.remove('is-error');

    var line = window.ZeptoATC.lineOf(variantId);
    var request = line
      ? window.ZeptoATC.change(line.key, target)
      : (target > 0 ? window.ZeptoATC.add(variantId, target) : Promise.resolve());

    request
      .then(function () { return window.ZeptoATC.refresh(); })
      .then(function () {
        delete pending[variantId];
        // the drawer stays shut by default so it never covers the sheet
        window.ZeptoATC.notify(shouldOpen);
        refreshActions();
      })
      .catch(function (err) {
        delete pending[variantId];
        row.classList.add('is-error');
        console.warn('[zepto-options]', err.message);
        window.ZeptoATC.refresh().then(refreshActions);
        setTimeout(function () { row.classList.remove('is-error'); }, 2500);
      })
      .finally(function () { row.classList.remove('is-loading'); });
  }

  /* ---------- open from any card button ---------- */
  document.addEventListener(
    'click',
    function (e) {
      var target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      var btn = target.closest('[data-zepto-options]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      open(btn.dataset.productHandle, btn.dataset.productUrl, btn.dataset.productTitle);
    },
    true
  );

  window.ZeptoOptions = { open: open, close: close };
  console.info('[zepto-options] JS loaded');
})();